import { BCMSRecord, DisabledPrompt, useBCMSContext, useBCMSModal, useStateManager } from "@bcm-dev/bcms-vite";
import EDEStatementsPageContainer from "components/EDEStatements/shared/containers/EDEStatementsPageContainer";
import EDEStatementsPageContentView from "components/EDEStatements/shared/views/EDEStatementsPageContentView";
import { CenterContentContainer, ContentHeaderView } from 'components/shared';
import AccordionSection from 'components/shared/views/AccordionSection';
import Classes, { Account, Document } from "model";
import React, { useEffect, useState } from "react";
import { ImageAsset } from "res";
import "./styles.css";
import "./mobile.css";


/**
 * @function EDEStatementsPage
 * @return {React.Component}
 * @exported true
 * @description The ede "Statemens" page.
 */
export default function EDEStatementsPage(props) {
  // Initalize all model classes
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);

  // Sets the values for the data and ui variables
  const data = { ...getDefaultPageData(), ...props.data ?? {} };
  const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };
  // The state to be passed to the state manager
  const state = { data, ui };
  
  // :: React.state, React.setState -> OBJECT
  // Function to initalize the controller for the state manager
  const controller = (state, setState) => ({
      data: state.data, // Set the data property of the controller
      ui: state.ui, // Set the ui property of the controller
      
  });
  
  return(
    <EDEStatementsPageContainer className={ `ede-statements-page` } state={ state } controller={ controller }>
        <PageContent/>
    </EDEStatementsPageContainer>
  );
}

/**
 * @name getDefaultPageData
 * @param {Void} 
 * @returns {OBJECT}
 * @description Hook to return the default data required by the page
 */
export const getDefaultPageData = () => {
  const accounts = Account.initAll().filter(item => item.type !== "unlinked");
  const accountTypeMap = {
    "checking": "Checking",
    "savings": "Savings",
    "loc": "Line of Credit",
    "cod": "Certificate of Deposit",
    "loan": "Loan"
  }

  // Builds an array of account type objects which 
  // we will use to build the content of the accordions.
  const accountTypes = accounts.reduce((acc, item) => {
    if(acc.length === 0) {
      acc.push({
        name: accountTypeMap[item.type],
        id: item.type,
        accounts: accounts.filter(account => account.type === item.type)
      });
    } else {
      var included = false;

      acc.map(_item => {
        if(_item.id.includes(item.type)) {
          included = true;
        }
        return _item;
      });

      if(!included) {
        acc.push({
          name: accountTypeMap[item.type],
          id: item.type,
          accounts: accounts.filter(account => account.type === item.type)
        });
      }
    }
    
    return acc;
  }, []);

  return {
      accounts,
      accountTypes
  }
};

/**
* @name getDefaultPageUI
* @param {Void} 
* @return {OBJECT}
* @description Hook to return the default ui required by the page.
*/
export const getDefaultPageUI = () => {
  const defaultAccount = Account.getDefaultBrandedAccountByType("checking");

  return {
    edeStatementModal: { isActive: false, modalID: "EDEStatementModal", accountID: defaultAccount.id },
    edeHistoryModal: { isActive: false, modalID: "EDEHistoryModal", accountID: defaultAccount.id },
    edeAssociateNoticesModal: { isActive: false, modalID: "EDEAssociateNoticesModal", accountID: defaultAccount.id },
    checkingAccordion: { isActive: true },
    savingsAccordion: { isActive: true },
    locAccordion: { isActive: true },
    codAccordion: { isActive: true },
    loanAccordion: { isActive: true },
    mobileMenu: { isActive: false }
  };
};

/**
 * @function AccountContent
 * @return {React.Component}
 * @exported false
 * @description The content for a single account, mostly the "View..." links.
 */
function AccountContent(props) {
  const account = props.account ?? {};
  const hasDocuments = Document.sortDateAscending(Document.initAll())
  .filter(item => item.account.id === account.id).length > 0;
  const viewHistoryLink = (
    hasDocuments
    ? <a className="clickable" onClick={ props.onViewHistoryClick(account.id) }>{ 
      <span data-point={ `EDE_Statements_Account_History_${ account.id }` }>{ `View History` }</span>
    }</a> 
    : <DisabledPrompt>
      <a className="clickable" onClick={ props.onViewHistoryClick(account.id) }>{ 
      <span data-point={ `EDE_Statements_Account_History_${ account.id }` }>{ `View History` }</span>
      }</a> 
    </DisabledPrompt>
  )

  return (
    <div className="link-content-container flex-container flex-vertical">
      <p className="account-label">{ 
      <span data-point={ `EDE_Statements_Account_${ account.id }` }>{ `${ account.name } - ${ account.number }` }</span>
      }</p>
      <a className="clickable" onClick={ props.onViewStatementsClick(account.id) }>{ 
      <span data-point={ `EDE_Statements_Account_View_Statement_${ account.id }` }>{ `View Statement` }</span>
      }</a> 
      { viewHistoryLink }
      <a className="clickable" onClick={ props.onViewAssociatedNoticesClick(account.id) }>{
      <span data-point={ `EDE_Statements_Account_View_Notices_${ account.id }` }>{ `View Associated Notices & Year-End Tax Forms` }</span>
      }</a> 
    </div>
  );
}

/**
 * @function PageContent
 * @return {React.Component}
 * @exported false
 * @description The content for this page.
 */
function PageContent(props) {
  const { data, ui } = useStateManager();
  const { close, open } = useBCMSModal();
  const subheader = `Statements are viewed in Adobe Acrobat Reader. Click the icon to download Adobe Acrobat Reader.`;
  const [ activeModalKey, setActiveModalKey ] = useState(undefined);

  const onViewStatementsClick = accountID => () => {
    open("EDEStatementModal", { data: { account: Account.initByID(accountID) } } );
  };

  const onViewHistoryClick = accountID => () => {
    open("EDEHistoryModal", { data: { account: Account.initByID(accountID) } } );
  };

  const onViewAssociatedNoticesClick = accountID => () => {
    open("EDEAssociateNoticesModal", { data: { account: Account.initByID(accountID) } } );
  };

  useEffect(() => {
    var currActiveModalKey = undefined;

    // We will only show 1 modal at a time, so find which one
    // is active in a capture and show it.
    Object.keys(ui).forEach((modalKey, i) => {
      if(modalKey.includes("Modal") && ui[modalKey].isActive) {
        currActiveModalKey = modalKey;
        setActiveModalKey(modalKey);
      }

      // Close an open modal if none are set to open in the ui object.
      if((i === Object.keys(ui).length - 1) && currActiveModalKey === undefined) {
        if(activeModalKey) {
          close(ui[activeModalKey].modalID);
        }
        setActiveModalKey(undefined);
      // Open a modal if one is set to open in the ui object.
      } else if((i === Object.keys(ui).length - 1) && currActiveModalKey) {
        if(currActiveModalKey) {
          const currAccount = Account.initByID(ui[currActiveModalKey].accountID);
          open(ui[currActiveModalKey].modalID, { data: { account: currAccount } } );
        }
      }
    });
  }, [ ui ]);

  const allAccountsContent = accountType => {
    const res = accountType.accounts.reduce((acc, account) => {
      acc.push(
        <AccountContent 
        key={ account.id }
        account={ account }
        onViewStatementsClick={ onViewStatementsClick }
        onViewHistoryClick={ onViewHistoryClick }
        onViewAssociatedNoticesClick={ onViewAssociatedNoticesClick } />
      );
      return acc;
    }, []);

    return res;
  }

  const content = data.accountTypes.reduce((acc, accountType) => {
    const accountTypeName = accountType.name.toLowerCase();

    acc.push(
      <AccordionSection key={ accountType.id } className="set-width custom-accordion-section"
      header={ accountType.name }  dataPoint={ `EDE_Statements_Account_Type_Section_${ accountType.id }` } defaultExpanded={ ui[`${ accountType.id }Accordion`].isActive }>
        { allAccountsContent(accountType) }
      </AccordionSection>
    );
    return acc;
  }, []);

  return (
    <EDEStatementsPageContentView>
      <div className="flex-container flex-vertical flex-width">
        <CenterContentContainer>
          <ContentHeaderView text="Statements" />
          <p className="subheader">{ subheader }</p>
          <div className="flex-horizontal">
            { content }
            <div className="adobe-acrobat-reader-icon-container">
              <DisabledPrompt>
                <ImageAsset id="AdobeAcrobatReaderPromoIcon" className={ "clickable" } />
              </DisabledPrompt>
            </div>
          </div>
        </CenterContentContainer>
      </div>
    </EDEStatementsPageContentView>
  );
}
